/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  SearchInput,
  Typography,
  useForm,
  useTheme,
  Table,
  Pagination,
  UtilityButton,
  Icon,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'react-use';
import { UserFilters, useSearchUsers } from 'api/useDirectOnrampApi';
import { QBLoader } from 'components';

type FilterStore = {
  filters: UserFilters;
  pagination: {
    page: number;
    size: number;
  };
} | null;

const PAGE_SIZE = 16;

const buildDefaultFilterFormValues = (store: FilterStore) =>
  ({
    emailAddress: store?.filters.emailAddress || null,
    userId: store?.filters.userId || null,
    walletAddress: store?.filters.walletAddress || null,
  } as UserFilters);

export const DirectOnrampUsersOverview = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();

  const [filterStore, setFilterStore] = useSessionStorage<FilterStore>(
    'qbDirectOnrampUserFilterStore'
  );

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<UserFilters>(
    buildDefaultFilterFormValues(filterStore),
    {}
  );

  useEffect(() => {
    setFilterStore({
      filters: fields,
      pagination: { page, size: PAGE_SIZE },
    });
  }, [fields, page, setFilterStore]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set(
      buildDefaultFilterFormValues({
        filters: {},
        pagination: { page: 1, size: PAGE_SIZE },
      })
    );
  };

  const headers = [
    { label: 'User ID', cellKey: 'userId', sort: false },
    { label: 'Email Address', cellKey: 'emailAddress', sort: false },
  ];

  const { data, isFetching, refetch } = useSearchUsers(fields, {
    page,
    size: PAGE_SIZE,
  });

  const rows = useMemo(() => {
    if (!data) return [];

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (data.aggregatedData.count <= PAGE_SIZE ? 1 : state));

    return Object.values(data.users).map((tx) => ({
      id: tx.userId,
      value: {
        ...tx,
      },
    }));
  }, [data]);
  if (!data) return <QBLoader />;

  return (
    <>
      <Header>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle">Users</Typography>
        </div>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto auto',
          gap: spacing.xxxs,
          alignItems: 'center',
        }}
      >
        <SearchInput
          placeholder="User ID"
          value={fields.userId ?? ''}
          onChange={(e) => setters.userId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Email Address"
          value={fields.emailAddress ?? ''}
          onChange={(e) => setters.emailAddress?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Wallet Address"
          value={fields.walletAddress ?? ''}
          onChange={(e) => setters.walletAddress?.set(e.target.value)}
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: '8fr 1fr',
          textWrap: 'nowrap',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xxxs,
            flexWrap: 'wrap',
          }}
        >
          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
          <UtilityButton
            variant="secondary"
            text="Refresh"
            onClick={() => refetch()}
            disabled={isFetching}
          />
          {isFetching && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Table
          headers={headers}
          rows={rows}
          // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
          onRowClick={(r) => navigate(`/direct-onramp/users/${r.userId}`)}
        />
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={data.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
